import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {useClientV2} from "../../utils/hooks";
import type {CreateEmailAutomationPayload} from "../../pages/dashboard/automations/email/_components/api-types";
import type {EmailAutomationApiItem} from "../../pages/dashboard/automations/email/_components/types";

export const EMAIL_AUTOMATIONS_QUERY_KEY = ["email-automations"] as const;
/** List cache for offering Automations step (full email catalog for Active + All tabs). */
export const EMAIL_AUTOMATIONS_OFFERING_STEP_KEY = [
  ...EMAIL_AUTOMATIONS_QUERY_KEY,
  "offering-step",
] as const;
export const EMAIL_AUTOMATION_OFFERINGS_QUERY_KEY = [
  "email-automation-offerings",
] as const;

type EmailAutomationError = {message?: string};

type OfferingCategory = "digitalProducts" | "productServices" | "liveEvents" | "services";

interface OfferingItem {
  id: number;
  name: string;
  imageUrl: string | null;
}

export function useGetEmailAutomationById(id: string | null) {
  const client = useClientV2();

  return useQuery<EmailAutomationApiItem, EmailAutomationError>({
    queryKey: ["email-automation-detail", id],
    queryFn: async () => {
      const res = (await client(`automation/email/${id}`)) as {
        data: EmailAutomationApiItem;
      };
      return res.data;
    },
    enabled: id !== null,
    refetchOnWindowFocus: false,
  });
}

export interface EmailAutomationAnalyticsData {
  autoEmailId: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  complaintRate: number;
}

export function useGetEmailAutomationAnalytics(id: string | number | null) {
  const client = useClientV2();

  return useQuery<EmailAutomationAnalyticsData, EmailAutomationError>({
    queryKey: ["email-automation-analytics", id],
    queryFn: async () => {
      const res = (await client(`automation/email/${id}/analytics`)) as {
        data: EmailAutomationAnalyticsData;
      };
      return res.data;
    },
    enabled: id !== null,
    refetchOnWindowFocus: false,
  });
}

export interface EmailAutomationRecipient {
  email: string;
  name: string;
  status: string;
  dateSent: string;
}

export interface GetEmailAutomationRecipientsResponse {
  items: EmailAutomationRecipient[];
  total: number;
  page: number;
  limit: number;
}

export const EMAIL_AUTOMATION_RECIPIENTS_QUERY_KEY = [
  "email-automation-recipients",
] as const;

export function useGetEmailAutomationRecipients(
  id: string | number | null,
  page: number = 1,
  limit: number = 10,
  status: string | null = null
) {
  const client = useClientV2();

  return useQuery<GetEmailAutomationRecipientsResponse, EmailAutomationError>({
    queryKey: [...EMAIL_AUTOMATION_RECIPIENTS_QUERY_KEY, id, page, limit, status],
    queryFn: async () => {
      let url = `automation/email/${id}/recipients?page=${page}&limit=${limit}`;
      if (status) {
        url += `&status=${status}`;
      }
      const res = (await client(url)) as {
        data: GetEmailAutomationRecipientsResponse;
      };
      return res.data;
    },
    enabled: id !== null,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
}

export type ResendEmailAutomationVariables = {
  id: string | number;
  recipientEmail: string;
  recipientName: string;
};

export function useResendEmailAutomation() {
  const client = useClientV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: ResendEmailAutomationVariables) =>
      client(`automation/email/${vars.id}/resend`, {
        method: "POST",
        data: {
          recipientEmail: vars.recipientEmail,
          recipientName: vars.recipientName,
        },
      }),
    onSuccess: () => {
      message.success("Email resent successfully");
    },
    onError: (error: EmailAutomationError) => {
      message.error(error?.message ?? "Failed to resend email");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: EMAIL_AUTOMATION_RECIPIENTS_QUERY_KEY,
      });
    },
  });
}

export interface GetEmailAutomationsResponse {
  items: EmailAutomationApiItem[];
  total: number;
  page: number;
  limit: number;
}

export function useGetEmailAutomations(page: number = 1, limit: number = 10) {
  const client = useClientV2();

  return useQuery<GetEmailAutomationsResponse, EmailAutomationError>({
    queryKey: [...EMAIL_AUTOMATIONS_QUERY_KEY, page, limit],
    queryFn: async () => {
      const res = (await client(`automation/email?page=${page}&limit=${limit}`)) as {
        data: GetEmailAutomationsResponse;
      };
      return res.data;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
}

export function useGetEmailAutomationsByTrigger(
  trigger: "after-purchase" | "abandoned-purchase" | null
) {
  const client = useClientV2();

  return useQuery<EmailAutomationApiItem[], EmailAutomationError>({
    queryKey: [...EMAIL_AUTOMATIONS_QUERY_KEY, "by-trigger", trigger],
    queryFn: async () => {
      const res = (await client(`automation/email?trigger=${trigger}`)) as {
        data: GetEmailAutomationsResponse;
      };
      return res.data.items;
    },
    enabled: trigger !== null,
    refetchOnWindowFocus: false,
  });
}

/**
 * All email automations for the offering Automations step (Active + All tabs).
 * One `GET automation/email` (no trigger, no pagination); backend should return the full list for this route.
 */
export function useGetAllEmailAutomationsForOfferingStep() {
  const client = useClientV2();

  return useQuery<EmailAutomationApiItem[], EmailAutomationError>({
    queryKey: [...EMAIL_AUTOMATIONS_OFFERING_STEP_KEY, "merged"],
    queryFn: async () => {
      const res = (await client("automation/email")) as {
        data: GetEmailAutomationsResponse;
      };
      return res.data.items;
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });
}

/** Query key helper for optimistic link/unlink + invalidation. */
export function getOfferingActiveEmailAutomationsQueryKey(offeringId: number) {
  return [...EMAIL_AUTOMATIONS_OFFERING_STEP_KEY, "active", offeringId] as const;
}

/**
 * Active email automations linked to a single offering (`GET automation/email/offering/{offeringId}`).
 */
export function useGetOfferingActiveEmailAutomations(
  offeringId: number | null | undefined
) {
  const client = useClientV2();
  const id = typeof offeringId === "number" && offeringId > 0 ? offeringId : null;

  return useQuery<EmailAutomationApiItem[], EmailAutomationError>({
    queryKey:
      id != null
        ? getOfferingActiveEmailAutomationsQueryKey(id)
        : ["offering-email-automations", "inactive"],
    queryFn: async () => {
      const res = (await client(`automation/email/offering/${id}`)) as {
        data: EmailAutomationApiItem[] | {items?: EmailAutomationApiItem[]};
      };
      if (Array.isArray(res.data)) return res.data;
      return res.data.items ?? [];
    },
    enabled: id != null,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });
}

export type LinkAutomationToOfferingVariables = {
  automationId: number;
  offeringId: number;
  offeringName: string;
  /** Used for optimistic active cache + pivot patch (same payload as hub row). */
  automation: EmailAutomationApiItem;
};

export type UnlinkAutomationFromOfferingVariables = {
  automationId: number;
  offeringId: number;
};

/** Shown when an automation would duplicate trigger + timing on this offering (client guard or server rejection). */
export const OFFERING_EMAIL_AUTOMATION_DUPLICATE_SCHEDULE_MESSAGE =
  "This offering already uses an automation with the same trigger and send timing. Choose a different automation or change timing on one of them first.";

function timingFingerprint(item: EmailAutomationApiItem): string {
  const settings = item.settings;
  const sendTime = settings?.sendTime ?? "immediate";
  const delay = sendTime === "immediate" ? 0 : Number(settings?.delayTime ?? 0);
  return `${item.trigger}|${sendTime}|${delay}`;
}

/** Whether `candidate` has the same API trigger + send delay as another linked automation on the offering. */
export function automationConflictsOfferingTriggerDelay(
  candidate: EmailAutomationApiItem,
  activeAutomationsOnOffering: EmailAutomationApiItem[]
): boolean {
  const fp = timingFingerprint(candidate);
  return activeAutomationsOnOffering.some(
    (linked) => linked.id !== candidate.id && timingFingerprint(linked) === fp
  );
}

function applyOfferingPivotOptimistic(
  item: EmailAutomationApiItem,
  offeringId: number,
  offeringName: string
): EmailAutomationApiItem {
  const existingPivot = item.settings?.pivot ?? [];
  if (existingPivot.some((p) => p.offeringId === offeringId)) {
    return item;
  }
  const pivotEntry = {
    id: Date.now(),
    offeringId,
    offeringName,
    createdAt: new Date().toISOString(),
  };
  if (item.settings) {
    return {
      ...item,
      settings: {
        ...item.settings,
        pivot: [...(item.settings.pivot ?? []), pivotEntry],
      },
    };
  }
  return {
    ...item,
    settings: {
      id: 0,
      sendTime: "immediate" as const,
      delayTime: 0,
      pivot: [pivotEntry],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

function linkMutationErrorMessage(err: unknown): string {
  const msg =
    err &&
    typeof err === "object" &&
    "message" in err &&
    typeof (err as {message: unknown}).message === "string"
      ? (err as {message: string}).message
      : "";
  const lower = msg.toLowerCase();
  const looksTimingConflict =
    (lower.includes("trigger") &&
      (lower.includes("delay") ||
        lower.includes("timing") ||
        lower.includes("schedule") ||
        lower.includes("same"))) ||
    lower.includes("duplicate");
  if (looksTimingConflict) return OFFERING_EMAIL_AUTOMATION_DUPLICATE_SCHEDULE_MESSAGE;
  return msg || "Could not attach this automation to the offering.";
}

function patchItemPivot(
  list: EmailAutomationApiItem[] | undefined,
  automationId: number,
  mutator: (item: EmailAutomationApiItem) => EmailAutomationApiItem
): EmailAutomationApiItem[] | undefined {
  if (!list) return list;
  return list.map((item) => (item.id === automationId ? mutator(item) : item));
}

export function useLinkAutomationToOffering() {
  const queryClient = useQueryClient();
  const client = useClientV2();

  return useMutation({
    mutationFn: (vars: LinkAutomationToOfferingVariables) =>
      client("automation/offerings", {
        method: "PUT",
        data: {
          autoEmailId: vars.automationId,
          offering: {
            offeringId: vars.offeringId,
            offeringName: vars.offeringName,
          },
        },
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({queryKey: EMAIL_AUTOMATIONS_OFFERING_STEP_KEY});
      const mergedKey = [...EMAIL_AUTOMATIONS_OFFERING_STEP_KEY, "merged"] as const;
      const activeKey = getOfferingActiveEmailAutomationsQueryKey(vars.offeringId);
      const previousMerged =
        queryClient.getQueryData<EmailAutomationApiItem[]>(mergedKey);
      const previousActive =
        queryClient.getQueryData<EmailAutomationApiItem[]>(activeKey);

      queryClient.setQueryData<EmailAutomationApiItem[]>(mergedKey, (old) => {
        const list = old ?? [];
        const hasRow = list.some((i) => i.id === vars.automationId);
        if (!hasRow) return list;
        return (
          patchItemPivot(list, vars.automationId, (item) =>
            applyOfferingPivotOptimistic(item, vars.offeringId, vars.offeringName)
          ) ?? list
        );
      });

      const nextMerged =
        queryClient.getQueryData<EmailAutomationApiItem[]>(mergedKey) ?? [];
      const patchedAutomation =
        nextMerged.find((a) => a.id === vars.automationId) ??
        applyOfferingPivotOptimistic(vars.automation, vars.offeringId, vars.offeringName);

      queryClient.setQueryData<EmailAutomationApiItem[]>(activeKey, (old) => {
        const existing = [...(old ?? [])];
        if (!existing.some((x) => x.id === patchedAutomation.id)) {
          existing.push(patchedAutomation);
        }
        return existing;
      });

      return {previousMerged, previousActive, mergedKey, activeKey};
    },
    onError: (err, _vars, context) => {
      if (context?.previousMerged !== undefined) {
        queryClient.setQueryData(context.mergedKey, context.previousMerged);
      }
      if (context?.previousActive !== undefined) {
        queryClient.setQueryData(context.activeKey, context.previousActive);
      }
      message.error(linkMutationErrorMessage(err));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_QUERY_KEY});
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_OFFERING_STEP_KEY});
    },
  });
}

export function useUnlinkAutomationFromOffering() {
  const queryClient = useQueryClient();
  const client = useClientV2();

  return useMutation({
    mutationFn: (vars: UnlinkAutomationFromOfferingVariables) =>
      client(`automation/email/${vars.automationId}/offering/${vars.offeringId}`, {
        method: "DELETE",
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({queryKey: EMAIL_AUTOMATIONS_OFFERING_STEP_KEY});
      const mergedKey = [...EMAIL_AUTOMATIONS_OFFERING_STEP_KEY, "merged"] as const;
      const activeKey = getOfferingActiveEmailAutomationsQueryKey(vars.offeringId);
      const previousMerged =
        queryClient.getQueryData<EmailAutomationApiItem[]>(mergedKey);
      const previousActive =
        queryClient.getQueryData<EmailAutomationApiItem[]>(activeKey);

      queryClient.setQueryData<EmailAutomationApiItem[]>(mergedKey, (old) => {
        const list = old ?? [];
        return patchItemPivot(list, vars.automationId, (item) => {
          if (!item.settings?.pivot) return item;
          const nextPivot = item.settings.pivot.filter(
            (p) => p.offeringId !== vars.offeringId
          );
          return {
            ...item,
            settings: {
              ...item.settings,
              pivot: nextPivot,
            },
          };
        });
      });

      queryClient.setQueryData<EmailAutomationApiItem[]>(activeKey, (old) =>
        (old ?? []).filter((x) => x.id !== vars.automationId)
      );

      return {previousMerged, previousActive, mergedKey, activeKey};
    },
    onError: (err, _vars, context) => {
      if (context?.previousMerged !== undefined) {
        queryClient.setQueryData(context.mergedKey, context.previousMerged);
      }
      if (context?.previousActive !== undefined) {
        queryClient.setQueryData(context.activeKey, context.previousActive);
      }
      const msg =
        err &&
        typeof err === "object" &&
        "message" in err &&
        typeof (err as {message: unknown}).message === "string"
          ? (err as {message: string}).message
          : "";
      message.error(msg || "Could not remove this automation from the offering.");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_QUERY_KEY});
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_OFFERING_STEP_KEY});
    },
  });
}

export function useCreateEmailAutomation() {
  const client = useClientV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateEmailAutomationPayload) =>
      client("automation/email", {
        method: "POST",
        data: payload,
      }),
    onSuccess: (data, variables) => {
      const action = variables.draft ? "saved as draft" : "created";
      message.success(`Email automation ${action}`);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_QUERY_KEY});
    },
  });
}

export function useUpdateEmailAutomation() {
  const client = useClientV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: CreateEmailAutomationPayload;
    }) =>
      client(`automation/email/${id}`, {
        method: "PUT",
        data: payload,
      }),
    onSuccess: (data, variables) => {
      const action = variables.payload.draft ? "saved as draft" : "updated";
      message.success(`Email automation ${action}`);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_QUERY_KEY});
    },
  });
}

export function useDeleteEmailAutomation() {
  const client = useClientV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) =>
      client(`automation/email/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      message.success("Email automation deleted");
    },
    onError: (error: EmailAutomationError) => {
      message.error(error?.message ?? "Failed to delete email automation");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_QUERY_KEY});
    },
  });
}

export type SetEmailAutomationPausedVariables = {
  id: string | number;
  isPaused: boolean;
};

export function useSetEmailAutomationPaused() {
  const client = useClientV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({id, isPaused}: SetEmailAutomationPausedVariables) =>
      client(`automation/email/${id}`, {
        method: "PUT",
        data: {isPaused},
      }),
    onSuccess: (_data, variables) => {
      message.success(variables.isPaused ? "Automation paused" : "Automation resumed");
    },
    onError: (error: EmailAutomationError) => {
      message.error(error?.message ?? "Failed to update automation status");
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_QUERY_KEY});
      void queryClient.invalidateQueries({queryKey: EMAIL_AUTOMATIONS_OFFERING_STEP_KEY});
      void queryClient.invalidateQueries({
        queryKey: ["email-automation-detail", String(variables.id)],
      });
    },
  });
}
